/**
 * The offshore isles — reward islands, opened by an achievement.
 *
 * The archipelago off the continent's coasts is the one part of this world with
 * a gate that stays shut, so it is worth being exact about what such a gate may
 * ever hold back.
 *
 * **An isle holds no mathematics, and cannot.** An `Isle` has no `conceptIds`,
 * no `regions`, no lesson and no boss; the type has nowhere to put one. That is
 * deliberate and it is the whole safety argument: a locked island cannot become
 * a place a student is kept away from mathematics, because there is no field in
 * which to write the mathematics down. What an isle holds is a drawing — a
 * cosmetic, a title, a view — which is exactly what gold already buys and
 * exactly what a kingdom's seal already withholds (`types.ts`: a seal withholds
 * the quest line, the boss ceremony and the reward, never a lesson).
 *
 * **The key is an achievement, which is earned by doing mathematics.** Not XP,
 * not level, not gold (§10: nothing unlocks on XP). `ACHIEVEMENTS` in
 * `packages/learning/src/progress.ts` is a list of things a student did —
 * mastered five concepts, kept a week's streak, answered twenty questions at
 * 90% — so an isle opens because of work rather than because of time served or
 * money spent. And an earned achievement is never revoked (`mergeProfiles`),
 * so an isle that has opened stays open: the sea does not take a reward back
 * because a review came due.
 *
 * **A locked isle is stated in words, never as a padlock alone.** `IsleState`
 * carries the requirement so a page can say "Needs: Master five concepts" and
 * name the badge; a bare lock icon is the version of this feature that tells a
 * student they are not good enough and declines to say why.
 *
 * Pure, and clock-free like everything else in this package: openness is a
 * function of which achievement ids a profile holds, and nothing else.
 */

/** What an isle gives up when it opens. Drawings and words — never a concept. */
export type IsleReward =
  /** A cosmetic from `ITEMS`, wearable in its slot like any other. */
  | { kind: 'item'; itemId: string }
  /** A title from `TITLES`, worn beside the learner's name. */
  | { kind: 'title'; titleId: string };

/**
 * One island off the coast.
 *
 * `requiresAchievementId` names an entry in `ACHIEVEMENTS`. One achievement per
 * isle rather than a clause list: a reward island is a small thing, and a
 * two-part unlock condition is a rule a student has to hold in their head
 * instead of a moment they can recognise.
 */
export type Isle = {
  id: string;
  name: string;
  emoji: string;
  /** A sentence about the place, shown whether or not it has opened. */
  flavor: string;
  /** The achievement that opens it. */
  requiresAchievementId: string;
  /** What is on it. May be empty: a view is a legitimate reward. */
  rewards: readonly IsleReward[];
};

/** Where an isle's landing stands on the overworld grid. */
export type IsleLandmark = {
  isleId: string;
  x: number;
  y: number;
};

/**
 * Whether an isle has opened, and what it is waiting for if it has not.
 *
 * Derived on every read like every other status in this package, because an
 * achievement can be earned between one render and the next and a stored flag
 * would be a second answer to a question the profile already answers.
 */
export type IsleState = {
  isleId: string;
  status: 'locked' | 'open';
  /** The achievement it wants; present whether or not it has been earned. */
  requiresAchievementId: string;
};

/**
 * The status of every isle, in the order they were authored.
 *
 * `earnedAchievementIds` is the profile's own list. Taking ids rather than a
 * `LearnerProfile` keeps this package from depending on the shape of one for a
 * question this simple — and makes the test for it a set of strings.
 */
export function isleStates(
  isles: readonly Isle[],
  earnedAchievementIds: Iterable<string>,
): IsleState[] {
  const earned = new Set(earnedAchievementIds);
  return isles.map((isle) => ({
    isleId: isle.id,
    status: earned.has(isle.requiresAchievementId) ? 'open' : 'locked',
    requiresAchievementId: isle.requiresAchievementId,
  }));
}

/** One isle's status. A convenience for a page drawing a single landing. */
export function isleState(isle: Isle, earnedAchievementIds: Iterable<string>): IsleState {
  return isleStates([isle], earnedAchievementIds)[0] as IsleState;
}

/** The isle with this id, or nothing. */
export const isleOf = (isles: readonly Isle[], isleId: string | undefined): Isle | undefined =>
  isleId === undefined ? undefined : isles.find((isle) => isle.id === isleId);

/** The isle landing standing on this tile, if any. */
export const isleLandmarkAt = (
  landings: readonly IsleLandmark[],
  position: { x: number; y: number },
): IsleLandmark | undefined =>
  landings.find((landing) => landing.x === position.x && landing.y === position.y);

/**
 * Every reward an open isle has handed over, as flat ids.
 *
 * The wardrobe asks "may this student wear this?" of `ITEMS`, so an isle's
 * cosmetics have to reach it as ids rather than as islands. Locked isles
 * contribute nothing, which is the entire mechanism.
 */
export function isleRewardIds(
  isles: readonly Isle[],
  earnedAchievementIds: Iterable<string>,
): { itemIds: string[]; titleIds: string[] } {
  const open = new Set(
    isleStates(isles, earnedAchievementIds)
      .filter((state) => state.status === 'open')
      .map((state) => state.isleId),
  );

  const itemIds: string[] = [];
  const titleIds: string[] = [];
  for (const isle of isles) {
    if (!open.has(isle.id)) continue;
    for (const reward of isle.rewards) {
      if (reward.kind === 'item') itemIds.push(reward.itemId);
      else titleIds.push(reward.titleId);
    }
  }
  return { itemIds, titleIds };
}
